import { DEFAULT_SETTINGS } from './constants.js';
import type { CompanySettings, Shift } from './types.js';

/**
 * How long a shift may run.
 *
 * A shift is recorded on the day it starts, and a day holds at most 24 hours,
 * so nothing longer can be stored as one shift. Real working days end well
 * before that: Icelandic law requires 11 hours' continuous rest in every 24
 * (Act 46/1980, art. 53), or 8 at a shift change, which caps a day at 13 or 16
 * hours of work.
 *
 * So a shift still open past the company's maximum is taken to be a forgotten
 * clock-out rather than a long day. It is closed with no hours recorded, and
 * the employer enters what was actually worked. Recording the elapsed time
 * would pay for hours nobody confirmed; recording a guess would too.
 */

/** The most hours one day can hold. */
export const DAY_HOURS = 24;

/**
 * The company's maximum, kept between its daily threshold and a full day.
 *
 * The floor matters as much as the ceiling: a maximum below the threshold
 * would close shifts before the "still working?" reminder had even fired.
 */
export function maxShiftHours(settings: Pick<CompanySettings, 'maxShiftHours' | 'dailyThreshold'>): number {
  const set = Number(settings.maxShiftHours);
  const max = Number.isFinite(set) && set > 0 ? set : DEFAULT_SETTINGS.maxShiftHours;
  const floor = Number(settings.dailyThreshold) || 0;
  return Math.min(DAY_HOURS, Math.max(floor, max));
}

/** Hours a shift has been open at `at`, in milliseconds since the epoch. */
export function hoursOpen(shift: Pick<Shift, 'clockInAt'>, at: number = Date.now()): number {
  return Math.max(0, (at - new Date(shift.clockInAt).getTime()) / 3_600_000);
}

/** True once a running shift has passed the company's maximum. */
export function isOverlong(
  shift: Pick<Shift, 'clockInAt'>,
  settings: Pick<CompanySettings, 'maxShiftHours' | 'dailyThreshold'>,
  at: number = Date.now(),
): boolean {
  return hoursOpen(shift, at) > maxShiftHours(settings);
}
